/**
 * ImportReviewList — review UI for fact-find document import.
 * Shows extracted entities grouped by section, each with a New / Merge badge, an
 * include toggle, and editable property values. The advisor confirms here before
 * anything is written to the graph.
 */
import React, { useMemo } from 'react';
import { Box, Typography, Checkbox, TextField, Chip, Tooltip } from '@mui/material';

import { WorkingEntity } from './hooks/useFactFindImport';

interface ImportReviewListProps {
  entities: WorkingEntity[];
  onToggle: (key: string) => void;
  onUpdateProp: (key: string, prop: string, value: string) => void;
}

const HIDDEN_PROPS = new Set([
  'sourceMeetingId',
  'sourceTranscriptionId',
  'sourceChunkId',
]);

function humanize(prop: string): string {
  return prop
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, (s) => s.toUpperCase());
}

const ImportReviewList: React.FC<ImportReviewListProps> = ({
  entities,
  onToggle,
  onUpdateProp,
}) => {
  const grouped = useMemo(() => {
    const byLabel = new Map<string, WorkingEntity[]>();
    entities.forEach((e) => {
      const list = byLabel.get(e.label) || [];
      list.push(e);
      byLabel.set(e.label, list);
    });
    return Array.from(byLabel.entries());
  }, [entities]);

  if (entities.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
        No fields could be extracted from this document.
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {grouped.map(([label, group]) => (
        <Box key={label}>
          <Typography
            sx={{
              fontSize: '0.72rem',
              fontWeight: 700,
              color: '#5f6368',
              textTransform: 'uppercase',
              letterSpacing: 0.6,
              mb: 1.5,
            }}
          >
            {label}
          </Typography>

          {group.map((e) => {
            const props = Object.entries(e.extraction.extracted_properties || {}).filter(
              ([k, v]) => !HIDDEN_PROPS.has(k) && v !== null && v !== '',
            );
            return (
              <Box
                key={e.key}
                sx={{
                  border: '1px solid #e0e3e7',
                  borderRadius: 1,
                  p: 2,
                  mb: 2,
                  bgcolor: e.include ? '#fff' : '#fafafa',
                  opacity: e.include ? 1 : 0.6,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Checkbox
                    size="small"
                    checked={e.include}
                    onChange={() => onToggle(e.key)}
                    sx={{ p: 0.25 }}
                  />
                  {e.isNew ? (
                    <Chip label="New" size="small" color="success" variant="outlined" />
                  ) : (
                    <Tooltip
                      title={`Merges into existing ${e.instanceId}`}
                      arrow
                    >
                      <Chip
                        label="Merge"
                        size="small"
                        color="warning"
                        variant="outlined"
                      />
                    </Tooltip>
                  )}
                  <Typography fontSize="0.8rem" fontWeight={600} color="#3c4043">
                    {e.instanceId === 'data' ? e.label : e.instanceId}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                    gap: 2,
                    pl: 3.5,
                  }}
                >
                  {props.map(([prop, val]) => (
                    <TextField
                      key={prop}
                      label={humanize(prop)}
                      value={String(val ?? '')}
                      size="small"
                      variant="outlined"
                      disabled={!e.include}
                      onChange={(ev) => onUpdateProp(e.key, prop, ev.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  ))}
                </Box>
              </Box>
            );
          })}
        </Box>
      ))}
    </Box>
  );
};

export default ImportReviewList;
